import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, BookOpen, Target, Image, CheckSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import WelcomeModal from "@/components/WelcomeModal";
import ProductTour from "@/components/ProductTour";
import MemoriesGallery from "@/components/MemoriesGallery";

const greetingByTime = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const reflectionPrompts = [
  "What felt most meaningful to us this week?",
  "What's one thing we can simplify together?",
  "How did we show up for each other today?",
  "What are we most grateful for right now?",
  "What's one goal that excites us both?",
];

export default function DashboardPage() {
  const { profile, partnerProfile, workspace } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState<any[]>([]);
  const [recentMeeting, setRecentMeeting] = useState<any>(null);
  const prompt = reflectionPrompts[Math.floor(Math.random() * reflectionPrompts.length)];

  const name1 = profile?.preferred_name || profile?.full_name?.split(" ")[0] || "You";
  const name2 = partnerProfile?.preferred_name || partnerProfile?.full_name?.split(" ")[0] || null;
  const greeting = name2
    ? `${greetingByTime()}, ${name1} & ${name2}.`
    : `${greetingByTime()}, ${name1}.`;

  useEffect(() => {
    if (!profile?.workspace_id) return;
    supabase
      .from("goals")
      .select("*, key_results(*)")
      .eq("workspace_id", profile.workspace_id)
      .order("created_at", { ascending: false })
      .limit(3)
      .then(({ data }) => setGoals(data || []));

    supabase
      .from("board_meetings")
      .select("*")
      .eq("workspace_id", profile.workspace_id)
      .eq("is_locked", true)
      .order("submitted_at", { ascending: false })
      .limit(1)
      .single()
      .then(({ data }) => setRecentMeeting(data));
  }, [profile?.workspace_id]);

  const cards = [
    { label: "Vision Board", icon: Image, to: "/app/vision", color: "bg-sage-light" },
    { label: "Our Goals", icon: Target, to: "/app/goals", color: "bg-terracotta-light" },
    { label: "Board Meeting", icon: BookOpen, to: "/app/reflect", color: "bg-sage-light" },
    { label: "To-Dos", icon: CheckSquare, to: "/app/todos", color: "bg-card" },
  ];

  return (
    <div className="space-y-10">
      <WelcomeModal />

      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">{greeting}</h1>
        <p className="mt-2 text-muted-foreground text-lg">Here's where you both stand this month.</p>
      </motion.div>

      {/* Memories Gallery - Hero */}
      <MemoriesGallery />

      {/* Reflection Prompt */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-sage-light border border-border rounded-xl p-6"
      >
        <div className="flex items-start gap-3">
          <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Reflection prompt</p>
            <p className="font-display text-lg italic text-foreground">"{prompt}"</p>
          </div>
        </div>
      </motion.div>

      {/* Product Tour */}
      <ProductTour />

      {/* Quick Nav Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((card, i) => (
          <motion.button
            key={card.to}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
            onClick={() => navigate(card.to)}
            className={`${card.color} border border-border rounded-xl p-4 text-left hover:shadow-md transition-shadow group`}
          >
            <card.icon className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors mb-2" />
            <p className="text-sm font-medium text-foreground">{card.label}</p>
          </motion.button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Goals Overview */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-card rounded-xl border border-border p-6 space-y-4"
        >
          <h2 className="font-display text-xl font-semibold">Our Progress</h2>
          {goals.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No goals yet. Create your first one together.</p>
          ) : (
            <div className="space-y-3">
              {goals.map((goal) => {
                const krs = goal.key_results || [];
                const avgProgress = krs.length > 0
                  ? Math.round(krs.reduce((s: number, kr: any) => s + kr.progress, 0) / krs.length)
                  : 0;
                return (
                  <div key={goal.id} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground font-medium">{goal.title}</span>
                      <span className="text-muted-foreground">{avgProgress}%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-primary rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${avgProgress}%` }}
                        transition={{ duration: 1, delay: 0.4 }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{goal.category}</p>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Recent Reflection */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-terracotta-light border border-border rounded-xl p-6"
        >
          {recentMeeting ? (
            <div className="flex items-start gap-3">
              <Heart className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" fill="currentColor" />
              <div>
                <p className="font-display text-lg italic text-foreground">
                  "{recentMeeting.went_well?.slice(0, 150)}{recentMeeting.went_well?.length > 150 ? "..." : ""}"
                </p>
                <p className="text-sm text-muted-foreground mt-2">— {recentMeeting.month}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <Heart className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" fill="currentColor" />
              <div>
                <p className="font-display text-lg italic text-foreground">
                  "Your first reflection will appear here after your first board meeting."
                </p>
                <p className="text-sm text-muted-foreground mt-2">— Getting started</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
